import {
	ArrayInput,
	AutocompleteInput,
	CheckboxGroupInput,
	FileField,
	FileInput,
	ImageField,
	ImageInput,
	NumberInput,
	ReferenceArrayInput,
	ReferenceInput,
	required,
	SelectArrayInput,
	SelectInput,
	SimpleFormIterator,
	TextInput,
	useTranslate
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import {
	Chip,
	Divider,
	Stack,
	Typography,
	useMediaQuery,
	useTheme
} from '@mui/material';
import {
	contactInputText,
	contactOptionText
} from '../../../../misc/ContactOption';
import { PriceByAreaInputs } from '../../../components/PriceByAreaInputs';
import { INPUT_SOURCE, OBJECTS_TYPES, SALE_RENT_TYPE } from '../../../constants';
import { AreaInputs } from '../AreaInputs';

const PlacementSectionInputs = () => {
	const translate = useTranslate();

	return (
		<Stack gap={1}>
			<Typography variant="h6">{translate('form.fields.placement')}</Typography>
			<TextInput
				source={INPUT_SOURCE.NAME}
				label={translate('form.fields.name')}
			/>
			<TextInput
				source="property_type"
				defaultValue={OBJECTS_TYPES.OFFICES}
				style={{ display: 'none' }}
			/>
			<ReferenceInput source={INPUT_SOURCE.COMPLEX_ID} reference="complexes">
				<AutocompleteInput
					optionText="name"
					label={translate('form.fields.complex')}
					onCreate={() => {}}
				/>
			</ReferenceInput>
			<ReferenceInput source={INPUT_SOURCE.BUILDING_ID} reference="buildings">
				<AutocompleteInput
					optionText="name"
					label={translate('form.fields.building')}
					onCreate={() => {}}
				/>
			</ReferenceInput>
			<ReferenceInput source={INPUT_SOURCE.COMPANY_ID} reference="companies">
				<AutocompleteInput
					optionText="name"
					label={translate('form.fields.managementCompany')}
					onCreate={() => {}}
					validate={required()}
				/>
			</ReferenceInput>
			<NumberInput
				source={INPUT_SOURCE.FLOOR}
				label={translate('form.fields.floor')}
				validate={required()}
			/>
			<SelectInput
				source={INPUT_SOURCE.STATE}
				label={translate('form.fields.state')}
				choices={[
					{ id: 1, name: 'Бетон' },
					{ id: 2, name: 'Ремонт після орендаря' },
					{
						id: 3,
						name: 'Новий базовий ремонт'
					},
					{ id: 4, name: 'Новий дизайнерський ремонт' }
				]}
				validate={required()}
			/>
			<AreaInputs />
			<CheckboxGroupInput
				source={INPUT_SOURCE.OBJECT_ACTIONS}
				label={translate('form.fields.objectActions')}
				choices={[
					{ id: SALE_RENT_TYPE.RENT, name: translate('form.fields.rent') },
					{ id: SALE_RENT_TYPE.SALE, name: translate('form.fields.sale') }
				]}
				helperText={false}
			/>
		</Stack>
	);
};

const DescriptionSectionInputs = () => {
	const { watch } = useFormContext();
	const objectActions: string[] = watch(INPUT_SOURCE.OBJECT_ACTIONS);
	const translate = useTranslate();

	return (
		<Stack gap={2}>
			{objectActions?.map(action => (
				<PriceByAreaInputs
					key={action}
					title={translate(`form.fields.${action}`)}
					priceType={action}
				/>
			))}
			<TextInput
				source={INPUT_SOURCE.FURNISHING}
				label={translate('form.fields.furnishing')}
				helperText={false}
				multiline={true}
			/>
		</Stack>
	);
};

const DocumentsSectionInputs = () => {
	const translate = useTranslate();

	return (
		<Stack gap={2}>
			<Typography variant="h6">Документи</Typography>
			<ImageInput
				label={translate('form.fields.images')}
				source={INPUT_SOURCE.IMAGES}
				multiple={true}
				placeholder={
					<p>Натисніть або перетягніть кілька фото, щоб завантажити</p>
				}
				helperText={false}
			>
				<ImageField source="src" title="title" />
			</ImageInput>
			<FileInput
				label={translate('form.fields.videos')}
				source={INPUT_SOURCE.VIDEOS}
				multiple={true}
				placeholder={
					<p>Натисніть або перетягніть кілька відео, щоб завантажити</p>
				}
				helperText={false}
				accept={{
					'video/*': []
				}}
			>
				<FileField source="src" title="title" />
			</FileInput>
			<ImageInput
				label={translate('form.fields.layout')}
				source={INPUT_SOURCE.LAYOUT}
				placeholder={<p>Натисніть або перетягніть фото, щоб завантажити</p>}
				helperText={false}
			>
				<ImageField source="src" title="title" />
			</ImageInput>
			<FileInput
				label={translate('form.fields.documents')}
				multiple={true}
				source={INPUT_SOURCE.DOCUMENTS}
				placeholder={
					<p>Натисніть або перетягніть кілька документів, щоб завантажити</p>
				}
				helperText={false}
				accept={{
					'text/*': []
				}}
			>
				<FileField source="src" title="title" />
			</FileInput>
			<SelectInput
				source={INPUT_SOURCE.CONFIGURATION}
				label={translate('form.fields.configuration')}
				choices={[
					{ id: 1, name: translate('form.fields.options.openPlanning') },
					{ id: 2, name: translate('form.fields.options.officePlanning') },
					{
						id: 3,
						name: translate('form.fields.options.mixedPlanning')
					}
				]}
			/>
		</Stack>
	);
};

export const ContactsSectionInputs = () => {
	const translate = useTranslate();

	return (
		<>
			<TextInput
				source={INPUT_SOURCE.COMMENTS}
				label={translate('form.fields.comments')}
				helperText={false}
				multiline={true}
			/>
			<ArrayInput
				source={INPUT_SOURCE.CONTACTS_JSONB}
				label={translate('form.fields.contacts')}
				helperText={false}
			>
				<SimpleFormIterator inline disableReordering>
					<ReferenceArrayInput
						source={INPUT_SOURCE.CONTACT_ID}
						reference="contacts_summary"
					>
						<AutocompleteInput
							label={translate('form.fields.contact')}
							optionText={contactOptionText}
							inputText={contactInputText}
							helperText={false}
							validate={required()}
							onCreate={() => {}}
						/>
					</ReferenceArrayInput>
					<ReferenceArrayInput
						resource="objects"
						source={INPUT_SOURCE.TAGS}
						reference="tags"
					>
						<SelectArrayInput
							label={translate('form.fields.roles')}
							optionText={({ name, color }) => (
								<Chip
									label={name}
									size="small"
									style={{
										backgroundColor: color,
										border: 0
									}}
								/>
							)}
							helperText={false}
							onCreate={() => {}}
							sx={{
								width: 200,
								minWidth: 200,
								'& .RaSelectArrayInput-chip': {
									backgroundColor: 'transparent'
								}
							}}
						/>
					</ReferenceArrayInput>
				</SimpleFormIterator>
			</ArrayInput>
			<Stack>
				<Typography variant="h6">{translate('form.fields.manager')}</Typography>
				<ReferenceInput
					source={INPUT_SOURCE.SALES_ID}
					reference="sales"
					filter={{
						'disabled@neq': true
					}}
				>
					<SelectInput
						label={translate('form.fields.manager')}
						helperText={false}
						optionText={choice => `${choice.first_name} ${choice.last_name}`}
						validate={required()}
					/>
				</ReferenceInput>
			</Stack>
		</>
	);
};

export const OfficeInputs = () => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	return (
		<Stack gap={2} p={1}>
			<Stack gap={3} direction={isMobile ? 'column' : 'row'}>
				<Stack gap={3} flex={5}>
					<PlacementSectionInputs />
					<DescriptionSectionInputs />
				</Stack>
				<Divider orientation={isMobile ? 'horizontal' : 'vertical'} flexItem />
				<Stack gap={2} flex={5}>
					{/*<DocumentsSectionInputs />*/}
					{/*<ContactsSectionInputs />*/}
				</Stack>
			</Stack>
		</Stack>
	);
};
